import { GenealogyEvent, EventParticipant, EventSourceCitation } from '../../types.ts';
import {
  fetchWithTimeout,
  parseErrorResponse,
  type ZelfMelden,
  eventFromServer,
  participantFromServer,
  citationFromServer,
} from './base.ts';

/**
 * A-10 — draag de genealogische waarschuwingen van de server mee.
 *
 * `eventFromServer` mapt alleen bekende kolommen, dus `warnings` viel eraf. De
 * events-routes waarschuwen nu bij een tegenstrijdige vitale datum (geboorte na
 * overlijden e.d.); zonder deze doorgifte zag de genealoog daar niets van.
 */
function withWarnings(event: GenealogyEvent, row: Record<string, unknown>): GenealogyEvent {
  if (Array.isArray(row.warnings) && row.warnings.length > 0) {
    (event as GenealogyEvent & { warnings?: unknown[] }).warnings = row.warnings;
  }
  return event;
}

export const eventsApi = {
  // === Events API ===

  getEvents: async (
    projectId: string,
    filters?: { personId?: string; relationshipId?: string; type?: string; placeId?: string }
  ): Promise<GenealogyEvent[]> => {
    const params = new URLSearchParams();
    if (filters?.personId) params.set('personId', filters.personId);
    if (filters?.relationshipId) params.set('relationshipId', filters.relationshipId);
    if (filters?.type) params.set('type', filters.type);
    if (filters?.placeId) params.set('placeId', filters.placeId);
    const qs = params.toString();
    // cache: 'no-store' — zie getPeople in people.ts (Refs #912).
    const res = await fetchWithTimeout(`/api/projects/${projectId}/events${qs ? '?' + qs : ''}`, {
      cache: 'no-store',
    });
    if (!res.ok) await parseErrorResponse(res);
    const rows = await res.json();
    return rows.map(eventFromServer);
  },

  getEvent: async (eventId: string): Promise<GenealogyEvent> => {
    const res = await fetchWithTimeout(`/api/events/${eventId}`);
    if (!res.ok) await parseErrorResponse(res);
    const row = await res.json();
    return eventFromServer(row);
  },

  saveEvent: async (
    event: Partial<GenealogyEvent>,
    options?: { sourceIds?: string[]; clientId?: string; expectedVersion?: string } & ZelfMelden
  ): Promise<GenealogyEvent> => {
    const isUpdate = !!event.id;
    const payload: Record<string, any> = {
      type: event.type,
      date: event.date,
      placeName: event.placeName,
      placeId: event.placeId,
      description: event.description,
      title: event.title,
      cause: event.cause,
      agency: event.agency,
      religion: event.religion,
      restriction: event.restriction,
      gedcomTag: event.gedcomTag,
      subtype: event.subtype,
      address: event.address,
      sortOrder: event.sortOrder,
      isNegated: event.isNegated,
    };
    // Forward-looking: include participants when provided (backend will support this in a future phase)
    if (event.participants !== undefined) {
      payload.participants = event.participants;
    }
    // #714: when sourceIds is provided, backend syncs event + sources atomically
    // in one transaction (eliminates race where reload sees event without sources).
    if (options?.sourceIds !== undefined) {
      payload.sourceIds = options.sourceIds;
    }

    if (isUpdate) {
      // #1826 — de versie die de aanroeper kent: de server geeft 409 als iemand anders de
      // gebeurtenis intussen wijzigde (routes/events.js). Alleen op verzoek: veel aanroepers
      // geven een heel (mogelijk verouderd) event mee.
      if (options?.expectedVersion) payload.expected_version = options.expectedVersion;
      const res = await fetchWithTimeout(`/api/events/${event.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (!res.ok) await parseErrorResponse(res, options);
      const row = await res.json();
      return withWarnings(eventFromServer(row), row);
    } else {
      const res = await fetchWithTimeout(`/api/projects/${event.projectId}/events`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        // #1825 — eigen id: een herhaling na een time-out maakt niets dubbel (200 i.p.v. 201).
        body: JSON.stringify(options?.clientId ? { ...payload, id: options.clientId } : payload),
      });
      if (!res.ok) await parseErrorResponse(res, options);
      const row = await res.json();
      return withWarnings(eventFromServer(row), row);
    }
  },

  deleteEvent: async (eventId: string, opts?: ZelfMelden): Promise<void> => {
    const res = await fetchWithTimeout(`/api/events/${eventId}`, { method: 'DELETE' });
    if (!res.ok) await parseErrorResponse(res, opts);
  },

  // === Event Participants ===

  addEventParticipant: async (
    eventId: string,
    participant: Partial<EventParticipant>
  ): Promise<EventParticipant> => {
    const res = await fetchWithTimeout(`/api/events/${eventId}/participants`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        personId: participant.personId,
        personName: participant.personName,
        role: participant.role,
        age: participant.age,
        notes: participant.notes,
      }),
    });
    if (!res.ok) await parseErrorResponse(res);
    const row = await res.json();
    return participantFromServer(row);
  },

  updateEventParticipant: async (
    participantId: string,
    data: Partial<EventParticipant>
  ): Promise<EventParticipant> => {
    const res = await fetchWithTimeout(`/api/event-participants/${participantId}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        role: data.role,
        age: data.age,
        notes: data.notes,
      }),
    });
    if (!res.ok) await parseErrorResponse(res);
    const row = await res.json();
    return participantFromServer(row);
  },

  deleteEventParticipant: async (participantId: string): Promise<void> => {
    const res = await fetchWithTimeout(`/api/event-participants/${participantId}`, {
      method: 'DELETE',
    });
    if (!res.ok) await parseErrorResponse(res);
  },

  // === Event Source Citations ===

  addEventSource: async (
    eventId: string,
    citation: Partial<EventSourceCitation>
  ): Promise<EventSourceCitation> => {
    const res = await fetchWithTimeout(`/api/events/${eventId}/sources`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        sourceId: citation.sourceId,
        page: citation.page,
        quality: citation.quality,
        dataDate: citation.dataDate,
        dataText: citation.dataText,
        notes: citation.notes,
      }),
    });
    if (!res.ok) await parseErrorResponse(res);
    const row = await res.json();
    return citationFromServer(row);
  },

  deleteEventSource: async (eventSourceId: string): Promise<void> => {
    const res = await fetchWithTimeout(`/api/event-sources/${eventSourceId}`, { method: 'DELETE' });
    if (!res.ok) await parseErrorResponse(res);
  },

  // === Events Bulk ===

  bulkCreateEvents: async (
    projectId: string,
    events: Array<
      Partial<GenealogyEvent> & {
        participants?: Partial<EventParticipant>[];
        sources?: Partial<EventSourceCitation>[];
      }
    >
  ): Promise<{ createdEvents: number; createdParticipants: number; createdSources: number }> => {
    const res = await fetchWithTimeout(`/api/projects/${projectId}/events/bulk`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ events }),
    });
    if (!res.ok) await parseErrorResponse(res);
    return res.json();
  },
};
